import classNames from 'classnames';
import * as React from 'react';
import Icon, { Style as IconStyle } from 'components/general/icon/icon';

// Type definitions
export type ButtonContext = 'Candidates' | 'Companies';
export type ButtonType =
  | 'Primary'
  | 'Secondary'
  | 'Terceary'
  | 'Primary-Icon'
  | 'Secondary-Icon'
  | 'Terceary-Icon';
export type ButtonStatus = 'Default' | 'Hovered' | 'Pressed' | 'Disabled';
export type ButtonSize = 'S' | 'M' | 'L';
export type ButtonMode = 'Light' | 'Dark';

interface IconOptions {
  color?: string;
  style?: IconStyle;
  className?: string;
}

interface ButtonProps {
  children: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  href?: string;
  target?: string;
  rel?: string;
  context?: ButtonContext;
  type?: ButtonType;
  size?: ButtonSize;
  mode?: ButtonMode;
  disabled?: boolean;
  className?: string;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  iconLeftName?: string;
  iconRightName?: string;
  iconStyle?: IconStyle;
  typeAttr?: 'button' | 'submit' | 'reset';
  'data-testid'?: string;
  spinner?: boolean;
}

const renderIcon = (
  customIcon?: React.ReactNode,
  iconName?: string,
  options?: IconOptions
): React.ReactNode => {
  if (customIcon) {
    return customIcon;
  }

  if (iconName) {
    return (
      <Icon
        name={iconName}
        color={options?.color}
        style={options?.style}
        className={options?.className}
      />
    );
  }

  return null;
};

const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  onKeyDown,
  href,
  target,
  rel,
  context = 'Candidates',
  type = 'Primary',
  size = 'M',
  mode = 'Light',
  disabled = false,
  className,
  iconLeft,
  iconRight,
  iconLeftName,
  iconRightName,
  iconStyle = IconStyle.Normal,
  typeAttr = 'button',
  'data-testid': testId,
  spinner,
}) => {
  const styles = require('./Button.module.scss');
  const [isPressed, setIsPressed] = React.useState(false);

  // Determine if icons are present
  const hasLeftIcon = !!(iconLeft || iconLeftName);
  const hasRightIcon = !!(iconRight || iconRightName);

  // Determine actual status based on disabled and pressed state
  const actualStatus = disabled ? 'Disabled' : isPressed ? 'Pressed' : 'Default';

  // Build CSS classes
  const buttonClasses = classNames(
    styles.button,
    styles[`context-${context.toLowerCase()}`],
    styles[`type-${type.toLowerCase().replace('-', '')}`],
    styles[`status-${actualStatus.toLowerCase()}`],
    styles[`size-${size}`],
    styles[`mode-${mode.toLowerCase()}`],
    {
      [styles.withLeftIcon]: hasLeftIcon,
      [styles.withRightIcon]: hasRightIcon,
    },
    className
  );

  const handleMouseDown = () => {
    if (!disabled) {
      setIsPressed(true);
    }
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  // Prepare icon options - color will be inherited from CSS
  const iconOptions: IconOptions = {
    style: iconStyle,
    className: styles.defaultIcon,
  };

  // Render icons
  const leftIconElement = renderIcon(iconLeft, iconLeftName, iconOptions);
  const rightIconElement = renderIcon(iconRight, iconRightName, iconOptions);

  // Common props shared between button and anchor elements
  const commonProps = {
    className: buttonClasses,
    'data-testid': testId,
    'data-context': context,
    'data-type': type,
    'data-status': actualStatus,
    'data-size': size,
    'data-mode': mode,
    'data-spinner': spinner,
  };

  // Button content (icons + text)
  const buttonContent = (
    <>
      {hasLeftIcon && <span className={styles.iconLeft}>{leftIconElement}</span>}
      <span className={styles.text}>{children}</span>
      {hasRightIcon && <span className={styles.iconRight}>{rightIconElement}</span>}
    </>
  );

  // Render as link if href is provided
  if (href) {
    const linkRel = rel || (target === '_blank' ? 'noopener noreferrer' : undefined);
    if (disabled) {
      return (
        <a
          {...commonProps}
          aria-disabled="true"
          tabIndex={-1}
          onClick={(event) => event.preventDefault()}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
        >
          {buttonContent}
        </a>
      );
    }
    return (
      <a
        href={href}
        target={target}
        rel={linkRel}
        {...commonProps}
        onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {buttonContent}
      </a>
    );
  }

  // Render as button
  return (
    <button
      type={typeAttr}
      onClick={onClick}
      disabled={disabled}
      {...commonProps}
      onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLButtonElement>}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {buttonContent}
    </button>
  );
};

export default Button;
